/**
 * DESIGN tab for the settings panel.
 * Particle effect configuration: thrusters, planet atmospheres, weapons, scenery.
 * Uses Imperial Forge styling via ForgeControls components.
 */

import { useParticleStore, ThrusterEffectType, AtmosphereEffectType } from '../../../stores/particleStore';
import { CollapsibleSection, ForgeSelect } from './ForgeControls';
import styles from './DesignTab.module.css';

interface DesignTabProps {
  readonly onOpenParticleEditor?: () => void;
}

// ---- Thruster effect options ----

const THRUSTER_OPTIONS = [
  { label: 'Plasma Exhaust', value: ThrusterEffectType.PLASMA_EXHAUST },
];

// ---- Atmosphere effect options ----

const ATMOSPHERE_OPTIONS = [
  { label: 'Default', value: AtmosphereEffectType.DEFAULT },
];

// ---- Main component ----

export function DesignTab({ onOpenParticleEditor }: DesignTabProps = {}) {
  const settings = useParticleStore((s) => s.settings);
  const updateThruster = useParticleStore((s) => s.updateThruster);
  const updatePlanetAtmosphere = useParticleStore((s) => s.updatePlanetAtmosphere);

  return (
    <div className={styles.container}>
      {onOpenParticleEditor && (
        <button
          type="button"
          className={styles.actionButton}
          onClick={onOpenParticleEditor}
          style={{ marginBottom: 8 }}
        >
          Open Particle Editor
        </button>
      )}
      <CollapsibleSection name="Particles" defaultExpanded={false}>
        <CollapsibleSection name="Fighters" defaultExpanded={false}>
          <ForgeSelect
            label="Forward Thrusters"
            value={settings.thrusters.forward}
            options={THRUSTER_OPTIONS}
            onChange={(v) => updateThruster('forward', v)}
          />
          <ForgeSelect
            label="Back Thrusters"
            value={settings.thrusters.back}
            options={THRUSTER_OPTIONS}
            onChange={(v) => updateThruster('back', v)}
          />
          <ForgeSelect
            label="Side Thrusters"
            value={settings.thrusters.side}
            options={THRUSTER_OPTIONS}
            onChange={(v) => updateThruster('side', v)}
          />
        </CollapsibleSection>

        <CollapsibleSection name="Planets" defaultExpanded={false}>
          <span className={styles.subLabel}>Atmosphere</span>
          <ForgeSelect
            label="Terran"
            value={settings.planetAtmospheres.terran}
            options={ATMOSPHERE_OPTIONS}
            onChange={(v) => updatePlanetAtmosphere('terran', v)}
          />
          <ForgeSelect
            label="Arid"
            value={settings.planetAtmospheres.arid}
            options={ATMOSPHERE_OPTIONS}
            onChange={(v) => updatePlanetAtmosphere('arid', v)}
          />
          <ForgeSelect
            label="Ocean"
            value={settings.planetAtmospheres.ocean}
            options={ATMOSPHERE_OPTIONS}
            onChange={(v) => updatePlanetAtmosphere('ocean', v)}
          />
          <ForgeSelect
            label="Tundra"
            value={settings.planetAtmospheres.tundra}
            options={ATMOSPHERE_OPTIONS}
            onChange={(v) => updatePlanetAtmosphere('tundra', v)}
          />
          <ForgeSelect
            label="Volcanic"
            value={settings.planetAtmospheres.volcanic}
            options={ATMOSPHERE_OPTIONS}
            onChange={(v) => updatePlanetAtmosphere('volcanic', v)}
          />
          <ForgeSelect
            label="Gas"
            value={settings.planetAtmospheres.gas}
            options={ATMOSPHERE_OPTIONS}
            onChange={(v) => updatePlanetAtmosphere('gas', v)}
          />
          <ForgeSelect
            label="Barren"
            value={settings.planetAtmospheres.barren}
            options={ATMOSPHERE_OPTIONS}
            onChange={(v) => updatePlanetAtmosphere('barren', v)}
          />
          <ForgeSelect
            label="Gaia"
            value={settings.planetAtmospheres.gaia}
            options={ATMOSPHERE_OPTIONS}
            onChange={(v) => updatePlanetAtmosphere('gaia', v)}
          />
        </CollapsibleSection>

        <CollapsibleSection name="Weapons" defaultExpanded={false}>
          <p className={styles.placeholderText}>Weapon effects coming in a future update.</p>
        </CollapsibleSection>

        <CollapsibleSection name="Scenery" defaultExpanded={false}>
          <p className={styles.placeholderText}>Scenery effects coming in a future update.</p>
        </CollapsibleSection>
      </CollapsibleSection>
    </div>
  );
}
